// KenoService.js
import { KENO_CONTRACT_ADDRESS } from '../config';
import kenoContractABI from '../kenoContractABI';
import web3Service from './Web3Service';

/* global BigInt */  // This tells ESLint that BigInt is defined globally

class KenoService {
  constructor() {
    this.web3 = null;
    this.web3Ws = null;
    this.contract = null;
    this.contractWs = null;
    this.account = null;
    this.eventSubscriptions = [];
    this.payoutTable = [];
  }

  // Initialize Keno contract
  async initialize() {
    if (!web3Service.web3 || !web3Service.account) {
      throw new Error('Web3Service must be initialized first');
    }
    
    try {
      this.web3 = web3Service.web3;
      this.web3Ws = web3Service.web3Ws;
      this.account = web3Service.account;
      
      // Initialize contract instances
      this.contract = new this.web3.eth.Contract(kenoContractABI, KENO_CONTRACT_ADDRESS);
      
      if (this.web3Ws) {
        this.contractWs = new this.web3Ws.eth.Contract(kenoContractABI, KENO_CONTRACT_ADDRESS);
      }
      
      // Load payout table
      await this.loadPayoutTable();
      
      return true;
    } catch (error) {
      console.error('Keno initialization error:', error);
      throw error;
    }
  }
  
  // Check if the network is connected - added to match web3Service
  isConnected() {
    return this.web3 !== null && this.account !== null;
  }
  
  // Load payout table from contract
  async loadPayoutTable() {
    try {
      if (this.contract) {
        const payoutTable = await this.contract.methods.getPayoutTable().call();
        this.payoutTable = payoutTable.map(multiplier => Number(multiplier) / 10000); // Convert from basis points to decimal
        return this.payoutTable;
      }
      return [];
    } catch (error) {
      console.error('Error loading payout table:', error);
      return [];
    }
  }
  
  // Get payout multiplier for specific hits
  getPayoutMultiplier(hits) {
    if (hits >= 0 && hits <= 10 && this.payoutTable.length > hits) {
      return this.payoutTable[hits];
    }
    return 0;
  }
  
  // Get contract data (balance, max bet, VRF fee)
  async getContractData() {
    if (!this.contract) {
      throw new Error('Contract not initialized');
    }
    
    try {
      const balance = await this.contract.methods.getHouseBalance().call();
      const max = await this.contract.methods.getMaxBet().call();
      const vrfFee = await this.contract.methods.calculateVRFFee().call();
      
      return { balance, max, vrfFee };
    } catch (error) {
      console.error('Error getting contract data:', error);
      throw error;
    }
  }
  
  // Check if user has a pending bet
  async hasPendingBet() {
    if (!this.contract || !this.account) {
      return false;
    }
    
    try {
      return await this.contract.methods.hasPendingBet(this.account).call();
    } catch (error) {
      console.error('Error checking pending bet:', error);
      throw error;
    }
  }
  
  // Place a Keno bet
  async placeBet(selectedNumbers, totalAmount) {
    if (!this.contract || !this.account) {
      throw new Error('Contract or account not initialized');
    }
    
    try {
      // Validate selected numbers
      if (selectedNumbers.length !== 10) {
        throw new Error('You must select exactly 10 numbers');
      }
      
      // Estimate gas to avoid errors
      const gasEstimate = await this.contract.methods.placeBet(selectedNumbers).estimateGas({
        from: this.account,
        value: totalAmount
      });
      
      const gasLimit = Math.ceil(Number(gasEstimate) * 1.2); // Add 20% buffer
      
      // Basic transaction options
      const txOptions = {
        from: this.account,
        value: totalAmount,
        gas: gasLimit
      };
      
      // Add priority fee to transaction options
      const txOptionsWithPriorityFee = await web3Service.addPriorityFee(txOptions);
      
      // Place the bet with priority fee
      const receipt = await this.contract.methods.placeBet(selectedNumbers).send(txOptionsWithPriorityFee);
      
      // Check for BetPlaced event in receipt
      if (receipt.events && receipt.events.BetPlaced) {
        return {
          success: true,
          requestId: receipt.events.BetPlaced.returnValues.requestId,
          txHash: receipt.transactionHash
        };
      }
      
      return { 
        success: true,
        txHash: receipt.transactionHash
      };
    } catch (error) {
      console.error('Error placing bet:', error);
      throw error;
    }
  }
  
  // Subscribe to contract events
  subscribeToEvents(onBetPlaced, onBetSettled) {
    // Clear any existing subscriptions
    this.clearEventSubscriptions();
    
    // IMPORTANT FIX: Check if contract instance and account exist
    if (!this.contract || !this.account) {
      console.log('Cannot subscribe to Keno events: Contract or account not initialized');
      return false;
    }
    
    try {
      // First, check if the websocket provider is actually connected
      const eventContract = (this.web3Ws && this.contractWs) ? this.contractWs : this.contract;
      
      console.log('Subscribing to Keno events using:', 
                 (this.web3Ws && this.contractWs) ? 'WebSocket provider' : 'HTTP provider');
      
      // Safely create event subscriptions
      let betPlacedSub = null;
      let betSettledSub = null;
      
      try {
        // BetPlaced event
        betPlacedSub = eventContract.events.BetPlaced({ filter: { player: this.account } })
          .on('data', event => {
            console.log('🎲 Keno BetPlaced event received:', event);
            if (onBetPlaced && typeof onBetPlaced === 'function') {
              onBetPlaced(event);
            }
          })
          .on('error', error => {
            console.error('❌ Keno BetPlaced event error:', error);
          });
      } catch (placeError) {
        console.error('Error subscribing to Keno BetPlaced events:', placeError);
        betPlacedSub = null;
      }
      
      try {
        // BetSettled event
        betSettledSub = eventContract.events.BetSettled({ filter: { player: this.account } })
          .on('data', event => {
            console.log('💰 Keno BetSettled event received:', event);
            if (onBetSettled && typeof onBetSettled === 'function') {
              onBetSettled(event);
            }
          })
          .on('error', error => {
            console.error('❌ Keno BetSettled event error:', error);
          });
      } catch (settleError) {
        console.error('Error subscribing to Keno BetSettled events:', settleError);
        betSettledSub = null;
      }
      
      // Only store valid subscriptions
      if (betPlacedSub) this.eventSubscriptions.push(betPlacedSub);
      if (betSettledSub) this.eventSubscriptions.push(betSettledSub);
      
      // Return success if at least one subscription was created
      return (betPlacedSub !== null || betSettledSub !== null);
    } catch (error) {
      console.error('Error subscribing to Keno events:', error);
      return false;
    }
  }
  
  // Get past bet settled events
  async getPastBetSettledEvents(requestId, fromBlock = 'latest') {
    if (!this.contract || !this.account) {
      throw new Error('Contract or account not initialized');
    }
    
    try {
      // If fromBlock is 'latest', try to get the current block number and look back
      if (fromBlock === 'latest') {
        const currentBlock = await this.web3.eth.getBlockNumber();
        // Convert to number to avoid BigInt issues
        const blockNumber = typeof currentBlock === 'bigint' ? Number(currentBlock) : currentBlock;
        fromBlock = Math.max(0, blockNumber - 5000);
      }
      
      const events = await this.contract.getPastEvents('BetSettled', {
        filter: { player: this.account },
        fromBlock: fromBlock,
        toBlock: 'latest'
      });
      
      if (requestId) {
        // Convert requestId to string for comparison to avoid BigInt mixing issues
        const requestIdStr = requestId.toString();
        
        // Find the event that matches our requestId
        return events.find(event => 
          event.returnValues.requestId.toString() === requestIdStr
        );
      }
      
      return events;
    } catch (error) {
      console.error('Error getting past events:', error);
      throw error;
    }
  }
  
  initializeWebSocketContract() {
    // Check if web3Ws exists and contractWs doesn't yet
    if (this.web3Ws && !this.contractWs && KENO_CONTRACT_ADDRESS) {
      try {
        this.contractWs = new this.web3Ws.eth.Contract(kenoContractABI, KENO_CONTRACT_ADDRESS);
        console.log('Keno WebSocket contract initialized');
        return true;
      } catch (error) {
        console.error('Error initializing Keno WebSocket contract:', error);
        return false;
      }
    }
    return false;
  }

  // Clean up event subscriptions
  clearEventSubscriptions() {
    for (const subscription of this.eventSubscriptions) {
      if (subscription && subscription.unsubscribe) {
        try {
          subscription.unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing from event:', error);
        }
      }
    }
    this.eventSubscriptions = [];
  }
  
  // Format utility
  fromWei(value) {
    return this.web3 ? this.web3.utils.fromWei(value, 'ether') : '0';
  }
  
  // Check if user is the contract owner
  async checkIfOwner() {
    if (!this.contract || !this.account) {
      return false;
    }
    
    try {
      const ownerAddress = await this.contract.methods.owner().call();
      return this.account.toLowerCase() === ownerAddress.toLowerCase();
    } catch (error) {
      console.error('Error checking owner status:', error);
      return false;
    }
  }
  
  // Add liquidity (owner only)
  async addLiquidity(amount) {
    if (!this.contract || !this.account) {
      throw new Error('Contract or account not initialized');
    }
    
    try {
      // Basic transaction options
      const txOptions = {
        from: this.account,
        value: amount,
        gas: 300000
      };
      
      // Add priority fee to transaction options
      const txOptionsWithPriorityFee = await web3Service.addPriorityFee(txOptions);
      
      const receipt = await this.contract.methods.addLiquidity().send(txOptionsWithPriorityFee);
      
      return {
        success: true,
        txHash: receipt.transactionHash
      };
    } catch (error) {
      console.error('Error adding liquidity:', error);
      throw error;
    }
  }
  
  // Remove liquidity (owner only)
  async removeLiquidity(amount) {
    if (!this.contract || !this.account) {
      throw new Error('Contract or account not initialized');
    }
    
    try {
      // Basic transaction options
      const txOptions = {
        from: this.account,
        gas: 300000
      };
      
      // Add priority fee to transaction options
      const txOptionsWithPriorityFee = await web3Service.addPriorityFee(txOptions);
      
      const receipt = await this.contract.methods.removeLiquidity(amount).send(txOptionsWithPriorityFee);
      
      return {
        success: true,
        txHash: receipt.transactionHash
      };
    } catch (error) {
      console.error('Error removing liquidity:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const kenoService = new KenoService();
export default kenoService;